"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { showToast } from "@/components/ui/sonner";
import { login } from "@/lib/api/generated";
import type { AuthResponse, LoginRequest } from "@/lib/api/model";
import { currentCometToken } from "@/lib/cometly";
import {
  appendCurrentInvitationToken,
  buildPostAuthRedirectPath,
  buildVerifyEmailPath,
} from "@/lib/invitation-links";
import { hashEmail } from "../lib/fingerprint";
import { setMfaChallenge } from "../lib/mfa-storage";
import { useFingerprintEventId } from "./use-fingerprint-event-id";
import { isMfaChallenge, isEmailVerificationRequired } from "./_helpers";

export function useLogin() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const getFingerprintEventId = useFingerprintEventId();

  return useMutation({
    // The login page runs the Cometly pixel — a returning user's fresh ad
    // click rides the login call and refreshes users.comet_token
    // (last-write-wins) so later conversions stitch to the newest click.
    mutationFn: async (data: LoginRequest) => {
      const emailHash = await hashEmail(data.email);
      return login({
        ...data,
        comet_token: currentCometToken(),
        // The hash attributes failed attempts in Fingerprint; the backend
        // links successful sign-ins to the user (linked_id = user id).
        fingerprint_event_id: await getFingerprintEventId(
          emailHash ? { tags: { email_sha256: emailHash } } : undefined,
        ),
      });
    },
    onSuccess: (data) => {
      if (isMfaChallenge(data)) {
        setMfaChallenge({
          pending_authentication_token: data.pending_authentication_token,
          authentication_challenge_id: data.authentication_challenge_id,
        });
        router.push(appendCurrentInvitationToken("/2fa"));
        return;
      }

      if (isEmailVerificationRequired(data)) {
        router.push(
          appendCurrentInvitationToken(
            buildVerifyEmailPath({
              email: data.email,
              pendingAuthenticationToken: data.pending_authentication_token,
            }),
          ),
        );
        return;
      }

      const auth = data as AuthResponse;

      if (!auth.user.email_verified) {
        router.push(
          appendCurrentInvitationToken(
            buildVerifyEmailPath({ email: auth.user.email }),
          ),
        );
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      router.push(buildPostAuthRedirectPath("/projects"));
    },
    onError: (error: Error) => {
      showToast.error("Sign in failed", {
        description: error.message || "Failed to sign in.",
      });
    },
  });
}
